import { Typography } from '@automata/ui';
import { AllInclusive } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { startCase } from 'lodash';
import { useMemo, useState } from 'react';
import { Position } from 'reactflow';

import { TIME_CONSTRAINT_HANDLE_END } from '../../../../constants';
import { toDataHandleId } from '../../../../features/helpers';
import type {
  ContinuousLoadingProperties,
  LabwareDataForTaskByHandlePairId,
} from '../../../../types';
import { DataHandleComponentData } from '../../../../types/taskNode';
import { DataHandleWithLabel } from '../../DataHandle';
import NodeBackground from '../../NodeBackground';
import TimeConstraintHandle from '../../TimeConstraintHandle';
import EmptyLabwareSection from '../TaskNode/EmptyLabwareSection';
import LabwareInTask from '../TaskNode/ExistingLabwareSection';
import { useLabwareHandleComponents } from '../TaskNode/utils';

type Props = {
  dataHandleIsConnectedByHandleID: { [handleID: string]: boolean };
  labwareDataByHandle: LabwareDataForTaskByHandlePairId;
  orderedHandlePairIds: string[];
  properties: ContinuousLoadingProperties;
  selected: boolean;
};

export const ContinuousLoadingNode = ({
  dataHandleIsConnectedByHandleID,
  labwareDataByHandle,
  orderedHandlePairIds,
  properties,
  selected,
}: Props): JSX.Element => {
  const [isHovered, setIsHovered] = useState(false);

  const labwareLabelledHandleComponents = useLabwareHandleComponents({
    instrumentCapacity: 1,
    labwareDataByHandle,
    orderedHandlePairIds,
    taskId: properties.id,
    taskType: properties.taskType,
  });

  const outputs: DataHandleComponentData[] = useMemo(() => {
    if (!properties.additionalDataInputs) {
      return [];
    }

    return Object.values(properties.additionalDataInputs).map((input) => {
      const handleId = toDataHandleId('source', properties.id, input.id);
      return {
        name: input.label || startCase(input.id),
        handleType: 'DATA_OUTPUT',
        handleId,
        dataKey: input.id,
        isConnected: dataHandleIsConnectedByHandleID?.[handleId] ?? false,
      } satisfies DataHandleComponentData;
    });
  }, [
    properties.additionalDataInputs,
    properties.id,
    dataHandleIsConnectedByHandleID,
  ]);

  return (
    <Stack
      direction="row"
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      pr={2}
    >
      {/* Label */}
      <Box
        textAlign="right"
        position="absolute"
        left={0}
        sx={{ transform: 'translate(-100%) translate(-0.5rem)' }}
        bgcolor="rgba(248, 248, 248, 0.8)"
        borderRadius={1}
        p={1}
      >
        <Typography variant="h6">
          {properties.nickname || 'Continuous Loading'}
        </Typography>
      </Box>

      {/* Node box & contents */}
      <NodeBackground isSelected={selected}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          minHeight="2.5rem"
        >
          <AllInclusive fontSize="large" aria-hidden="true" />

          <>
            <Divider orientation="vertical" flexItem />
            {labwareLabelledHandleComponents.map((data) => {
              if (data.handleType === 'labware') {
                return (
                  <LabwareInTask
                    key={`labware-${data.handlePairId}`}
                    hasDeadEnd={false}
                    handlePairId={data.handlePairId}
                    data={data.labwareData}
                  />
                );
              }
              if (data.handleType === 'addLabware') {
                return (
                  <EmptyLabwareSection
                    key={`addLabware-${data.handlePairId}`}
                    hasDeadEnd={false}
                    handlePairId={data.handlePairId}
                  />
                );
              }
              data satisfies never;
            })}
          </>

          {outputs.length > 0 && <Divider orientation="vertical" flexItem />}
          {outputs.map((data) => (
            <DataHandleWithLabel
              key={data.handleId}
              name={data.name}
              id={data.handleId}
              position={Position.Bottom}
              handleStyle={{ bottom: -23 }}
              isConnected={data.isConnected}
            />
          ))}
        </Stack>
      </NodeBackground>

      {/* Time Constraint handles */}
      <Box sx={{ transform: `translate(1rem)` }}>
        <TimeConstraintHandle
          id={TIME_CONSTRAINT_HANDLE_END}
          taskId={properties.id}
          style={{ top: '80%' }}
          nodeIsHovered={isHovered}
          nodeIsSelected={selected}
        />
      </Box>
    </Stack>
  );
};
